import { isNil } from 'lodash';
import { safeSubtract } from './math';

/**
 * The arithmetic behind a quote's contribution ratio (TMS-2765). It lives in
 * the shared package so the server and the quote page run one implementation.
 *
 * ratio = round1(clamp((revenue - driverPay) / benchmark * 100, 0, 999))
 *
 * `benchmark` is what the company's own tariffs charge for the quote; the
 * backend produces it by rating the quote with its freight billed by tariff.
 * This file knows nothing about where the benchmark comes from and holds no
 * rates: see `docs/codebase-library/quote-contribution-ratio.md`.
 *
 * Not the Intelligence Board's "contribution ratio", which is revenue over
 * variable cost for a customer across a date range.
 */

/** The ratio is capped so one near-zero benchmark cannot print a five-digit score. */
export const RATIO_CAP = 999;
/** A contribution below zero reads as 0, not as a negative score. */
export const RATIO_FLOOR = 0;
export const RATIO_DECIMALS = 1;

/**
 * The cutoffs a company starts with until it sets its own on Settings > Orders.
 * A quote priced at its tariff scores 100 x (1 - the driver's share of revenue),
 * so 30 reads "we keep at least 30% of the tariff price after paying the
 * driver". Starting points, not derived from any customer's figures.
 */
export const DEFAULT_MARGINAL_FROM_RATIO = 15;
export const DEFAULT_PROFITABLE_FROM_RATIO = 30;

/** What a company may set either cutoff to. */
export const QUOTE_CONTRIBUTION_CUTOFF_BOUNDS = {
  minimum: RATIO_FLOOR,
  maximum: RATIO_CAP,
} as const;

/**
 * Ceiling on a quote's driver pay and on the revenue the card sends, in USD
 * cents ($20,000,000). Both travel as GraphQL `Int` and the driver pay is
 * stored in a Postgres INTEGER, so a larger value fails as an unhandled
 * out-of-range error instead of a message the user can act on.
 */
export const MAXIMUM_QUOTE_AMOUNT_USD_CENTS = 2_000_000_000;

/** The two ratios that split a scored quote into its three coloured stages. */
export type ContributionRatioCutoffs = {
  marginalFrom: number;
  profitableFrom: number;
};

/**
 * Where a quote's ratio falls:
 * - LOSS: ratio 0. The revenue does not cover the driver pay; the ratio floors
 *   at 0, so a negative contribution lands here too.
 * - NOT_PROFITABLE: above 0 but under `marginalFrom`.
 * - MARGINAL: from `marginalFrom` up to `profitableFrom`.
 * - PROFITABLE: at or above `profitableFrom`.
 * - UNAVAILABLE: no ratio, because no tariff priced the quote.
 * - DRIVER_PAY_REQUIRED: no ratio, because nobody has entered the driver pay.
 *   A quote priced at its tariff with a driver pay of nothing would score 100
 *   and read as profitable on every quote, which says nothing; the card asks
 *   for the figure instead. An entered zero is a figure and is scored.
 */
export type ContributionStatus =
  | 'LOSS'
  | 'NOT_PROFITABLE'
  | 'MARGINAL'
  | 'PROFITABLE'
  | 'UNAVAILABLE'
  | 'DRIVER_PAY_REQUIRED';

/**
 * The cutoffs in force for a company: its own where set, the defaults where
 * not. Each falls back on its own, so a company that set only one keeps it.
 */
export const resolveContributionRatioCutoffs = ({
  marginalFrom,
  profitableFrom,
}: {
  marginalFrom: number | null | undefined;
  profitableFrom: number | null | undefined;
}): ContributionRatioCutoffs => ({
  marginalFrom: marginalFrom ?? DEFAULT_MARGINAL_FROM_RATIO,
  profitableFrom: profitableFrom ?? DEFAULT_PROFITABLE_FROM_RATIO,
});

/**
 * Clamps, then rounds to one decimal.
 *
 * The floor and the rounding meet at zero: a contribution small enough to round
 * to 0.0 reads as a loss. That keeps the colour consistent with the number
 * shown, which matters more than a tenth of a point at break-even.
 *
 * The benchmark is divided by as given. Do not round it to cents first: an
 * intermediate rounded before the division moves the one-decimal ratio.
 *
 * @returns The ratio, or null when it cannot be stated: the benchmark is not
 * positive, or the driver pay has not been entered (null). A driver pay of
 * zero is an entered figure and is scored.
 */
export const computeContributionRatio = ({
  revenueUsd,
  driverPayUsd,
  benchmarkUsd,
}: {
  revenueUsd: number;
  driverPayUsd: number | null;
  benchmarkUsd: number | null;
}): number | null => {
  if (isNil(benchmarkUsd) || benchmarkUsd <= 0 || isNil(driverPayUsd)) {
    return null;
  }
  const contributionUsd = safeSubtract(revenueUsd, driverPayUsd);
  const raw = (contributionUsd / benchmarkUsd) * 100;
  const clamped = Math.min(RATIO_CAP, Math.max(RATIO_FLOOR, raw));
  const factor = 10 ** RATIO_DECIMALS;
  return Math.round(clamped * factor) / factor;
};

/**
 * "No benchmark" wins over "driver pay required": with no tariff price there
 * is nothing to measure a driver pay against, so asking for one would send the
 * user to fill a box that cannot produce a ratio.
 */
export const statusForRatio = ({
  ratio,
  cutoffs,
  benchmarkUsd,
  driverPayUsd,
}: {
  ratio: number | null;
  cutoffs: ContributionRatioCutoffs;
  benchmarkUsd: number | null;
  driverPayUsd: number | null;
}): ContributionStatus => {
  if (isNil(benchmarkUsd) || benchmarkUsd <= 0) {
    return 'UNAVAILABLE';
  }
  if (isNil(driverPayUsd)) {
    return 'DRIVER_PAY_REQUIRED';
  }
  if (isNil(ratio)) {
    return 'UNAVAILABLE';
  }
  if (ratio <= RATIO_FLOOR) {
    return 'LOSS';
  }
  if (ratio >= cutoffs.profitableFrom) {
    return 'PROFITABLE';
  }
  return ratio >= cutoffs.marginalFrom ? 'MARGINAL' : 'NOT_PROFITABLE';
};
